<script setup lang="ts">
import ButtonClose from '@/components/button/ButtonClose.vue'
import {
    alertMessageEmptyHappy,
    alertMessageHappy,
    showAlertMessageHappy
} from '@/happyTheme/event/dashboard/alert/AlertMessageHappy'
import { useI18n } from 'vue-i18n'

const { t, te } = useI18n()

// Translate a backend key if it exists in the i18n catalog,
// otherwise return the raw string (fallback for legacy messages).
function tr(key: string): string {
    if (!key) return ''
    return te(key) ? t(key) : key
}

function toggleAlertMessageHappy() {
    showAlertMessageHappy.value = !showAlertMessageHappy.value
    setTimeout(() => {
        alertMessageHappy.value = alertMessageEmptyHappy
    }, 300)
}
</script>

<template>
    <section class="section-alert-message-dashboard-happy">
        <section
            class="alert-message-dashboard-overlay-happy"
            @click="toggleAlertMessageHappy()"
        ></section>

        <Transition
            name="message-happy"
            enter-active-class="transition-all duration-300 ease-out"
            enter-from-class="translate-x-full"
            enter-to-class="translate-x-0"
            leave-active-class="transition-all duration-300 ease-in"
            leave-from-class="translate-x-0"
            leave-to-class="translate-x-full"
        >
            <section
                v-if="
                    alertMessageHappy.errors &&
                    alertMessageHappy.errors.length !== 0 &&
                    showAlertMessageHappy
                "
                class="section-alert-message-content-dashboard-happy"
            >
                <div
                    class="div-alert-message-content-dashboard-happy"
                    :class="`div-content-message-content-dashboard-happy-${alertMessageHappy.class}`"
                >
                    <h4 class="h4-alert-message-dashboard-happy">
                        {{ tr(alertMessageHappy.title) }}
                    </h4>
                    <ButtonClose
                        :function="toggleAlertMessageHappy"
                        :hover-color="
                            alertMessageHappy.class === 'success'
                                ? 'success-happy'
                                : 'error-happy'
                        "
                    />
                </div>
                <div
                    class="div-alert-message-second-content-errors-dashboard-happy"
                    :class="alertMessageHappy.class"
                >
                    <p
                        v-for="(error, i) in alertMessageHappy.errors"
                        :key="i"
                        class="bullet"
                    >
                        {{ tr(error) }}
                    </p>
                </div>
            </section>

            <section
                class="section-alert-message-content-dashboard-happy"
                v-else-if="
                    showAlertMessageHappy &&
                    alertMessageHappy.errors &&
                    alertMessageHappy.errors.length === 0
                "
            >
                <div
                    class="div-alert-message-content-dashboard-happy"
                    :class="`div-content-message-content-dashboard-happy-${alertMessageHappy.class}`"
                >
                    <h4 class="h4-alert-message-dashboard-happy">
                        {{ tr(alertMessageHappy.title) }}
                    </h4>
                    <ButtonClose
                        :function="toggleAlertMessageHappy"
                        :hover-color="
                            alertMessageHappy.class === 'success'
                                ? 'success-happy'
                                : 'error-happy'
                        "
                    />
                </div>

                <div
                    class="div-alert-message-second-content-dashboard-happy"
                    :class="alertMessageHappy.class"
                >
                    <p class="p-alert-message-dashboard-happy">
                        {{ tr(alertMessageHappy.message) }}
                    </p>
                </div>
            </section>
        </Transition>
    </section>
</template>

<style scoped>
.section-alert-message-dashboard-happy {
    @apply fixed top-0 left-0 w-full h-full z-40 flex justify-center;
}

.alert-message-dashboard-overlay-happy {
    @apply w-full h-full;
    /* @apply bg-text-dark-happy-light opacity-40; */
}
.section-alert-message-content-dashboard-happy {
    @apply z-50 absolute min-w-72 max-w-[22rem] flex flex-col justify-center;
    /* @apply mt-52; */
    @apply bottom-10 right-2;
}
.div-alert-message-content-dashboard-happy {
    @apply w-full h-full rounded-t-md flex justify-between items-center gap-2 p-3 shadow shadow-text-dark-happy-light;
}
.div-content-message-content-dashboard-happy-success {
    /* refactored: per-theme success header -> semantic accent-hover */
    @apply bg-t-accent-hover text-t-on-accent;
    /* refactored — ifo-dark: yumuşak accent/10 bg + accent text (pill card pattern). */
    @apply happy-ifo-dark:bg-t-accent/10
           happy-ifo-dark:!text-t-accent
           happy-ifo-dark:border
           happy-ifo-dark:border-t-accent/40
           happy-ifo-dark:border-b-0;
}

.div-content-message-content-dashboard-happy-error {
    /* refactored: per-theme error header -> semantic danger */
    @apply bg-t-danger text-t-on-accent;
    /* refactored — ifo-dark: dark bg + danger text + danger/40 border. */
    @apply happy-ifo-dark:bg-t-bg
           happy-ifo-dark:!text-t-danger
           happy-ifo-dark:border
           happy-ifo-dark:border-t-danger/40
           happy-ifo-dark:border-b-0;
}

.div-alert-message-second-content-dashboard-happy {
    @apply w-full h-full rounded-b-md px-3 py-4 text-center shadow shadow-text-dark-happy-light;
}
.div-alert-message-second-content-errors-dashboard-happy {
    @apply w-full h-full rounded-b-md px-3 py-4 shadow shadow-text-dark-happy-light;
}

.success {
    /* refactored: per-theme success body -> semantic accent */
    @apply bg-t-accent text-t-on-accent;
    @apply happy-purple-gradient:bg-gradient-to-t happy-purple-gradient:to-bg-purple-dark happy-purple-gradient:from-bg-purple-light;
    /* refactored — ifo-dark: dark card + accent/20 fill + accent text. */
    @apply happy-ifo-dark:bg-t-accent/5
           happy-ifo-dark:!text-t-text
           happy-ifo-dark:border
           happy-ifo-dark:border-t-accent/40
           happy-ifo-dark:border-t-0
           happy-ifo-dark:shadow-[0_0_18px_rgba(212,160,23,0.10)];
}
.error {
    /* refactored: per-theme error body -> semantic danger */
    @apply bg-t-danger text-t-on-accent;
    /* refactored — ifo-dark: dark card + danger/10 wash + text-t-text. */
    @apply happy-ifo-dark:bg-t-danger/10
           happy-ifo-dark:!text-t-text
           happy-ifo-dark:border
           happy-ifo-dark:border-t-danger/40
           happy-ifo-dark:border-t-0;
}

.h4-alert-message-dashboard-happy {
    @apply font-semibold text-start;
}
.h4-alert-message-dashboard-happy-error {
    @apply text-bg-happy-light;
}

.p-alert-message-dashboard-happy {
    @apply text-base font-medium text-center;
}

.bullet {
    @apply relative pl-4;
}

.bullet::before {
    content: '•';
    position: absolute;
    left: 0;
}
</style>
